import { useFeatureFlags } from './useFeatureFlags';
import { ISidebarItem } from '../utils/interfaces';
import customersIcon from '../assets/customers.svg';
import usersIcon from '../assets/users.svg';
import assetsIcon from '../assets/vehicles.svg';
import containerIcon from '../assets/container.svg';
import cylinderIcon from '../assets/cylinder.svg';
import stockIcon from '../assets/store.svg';
import shopSellingIcon from '../assets/shopselling.svg';
import tripsIcon from '../assets/trips.svg';
import assignmentsIcon from '../assets/assignments.svg';
import varianceIcon from '../assets/variance.svg';
import paymentsIcon from '../assets/payments.svg';
import pricingIcon from '../assets/pricing.svg';
import configsIcon from '../assets/configs.svg';
import { useMemo, useState } from 'react';
import { hasAnyPermission, useSubscriptions } from '@prosaas/psw-utils';
import { AUTH_PERMISSIONS } from '@prosaas/psw-events';

interface ISidebarItemsHook {
  isAuthorized: boolean;
  sidebarItemList: ISidebarItem[];
}

export function useSidebarItems(): ISidebarItemsHook {
  const { hasShopSellingFeatureEnabled, hasProductListFeatureEnabled } = useFeatureFlags();

  const [userPermissions, setUserPermissions] = useState<string[] | null>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  useSubscriptions([
    [
      AUTH_PERMISSIONS,
      (permissions) => {
        setUserPermissions(permissions || []);
        setIsAuthorized(permissions && permissions.length > 0);
      },
    ],
  ]);

  const navigationDictionary = {
    customers: {
      'navigation.backOffice.customers': 'customers',
      'navigation.backOffice.customers.types': 'customers/types',
      'navigation.backOffice.customers.deleted': 'customers/deleted',
    },
    users: {
      'navigation.backOffice.users': 'users',
      'navigation.backOffice.users.types': 'users/types',
      'navigation.backOffice.users.roles': 'users/roles',
      'navigation.backOffice.users.availability': 'users/availability',
    },
    vehicles: {
      'navigation.backOffice.vehicles': 'vehicles',
      'navigation.backOffice.vehicles.types': 'vehicles/types',
      'navigation.backOffice.vehicles.categories': 'vehicles/categories',
    },
    containers: {
      'navigation.backOffice.containers': 'containers',
      'navigation.backOffice.containers.types': 'containers/types',
    },
    stockAdmin: hasProductListFeatureEnabled
      ? {
          'navigation.backOffice.stockAdmin': 'stock-admin/product/list',
          //TODO: update with proper route once the permission is in place, also add the permission for the product types route
          'navigation.backOffice.stockAdmin.categories': 'stock-admin/product/categories',
          'navigation.backOffice.stockAdmin.capacities': 'stock-admin/product/capacities',
          'navigation.backOffice.stockAdmin.states': 'stock-admin/product/states',
          'navigation.backOffice.stockAdmin.saleTypes': 'stock-admin/product/sale-types',
        }
      : {
          'navigation.backOffice.stockAdmin': 'stock-admin',
          'navigation.backOffice.stockAdmin.categories': 'stock-admin/product/categories',
          'navigation.backOffice.stockAdmin.capacities': 'stock-admin/product/capacities',
          'navigation.backOffice.stockAdmin.states': 'stock-admin/product/states',
          'navigation.backOffice.stockAdmin.saleTypes': 'stock-admin/product/sale-types',
        },

    stockFlow: {
      'navigation.backOffice.stockFlow': 'stock-flow',
      'navigation.backOffice.stockFlow.fillingOrders': 'stock-flow/filling-orders',
      'navigation.backOffice.stockFlow.defectives': 'stock-flow/defectives',
      'navigation.backOffice.stockFlow.stockMovement': 'stock-flow/stock-movement',
      'navigation.backOffice.stockFlow.manufacturing': 'stock-flow/manufacturing-orders',
      'navigation.backOffice.stockFlow.stockIntroduction': 'stock-flow/stock-introduction-orders',
    },
    shopSelling: {
      'navigation.backOffice.shopSelling.shops': 'shop-selling/shops',
    },
    trips: {
      'navigation.backOffice.trips.active': 'trips/active',
      'navigation.backOffice.trips.pending': 'trips/pending',
      'navigation.backOffice.trips.past': 'trips/past',
    },
    assignments: {
      'navigation.backOffice.assignments.vehicles': 'assignments/vehicles',
      'navigation.backOffice.assignments.containers': 'assignments/containers',
      'navigation.backOffice.assignments.tillNumbers': 'assignments/till-numbers',
    },
    variance: {
      'navigation.backOffice.variance.active': 'variance/active',
      'navigation.backOffice.variance.past': 'variance/past',
    },
    payments: {
      'navigation.backOffice.payments': 'payments/receipts',
      'navigation.backOffice.payments.creditNotesList': 'payments/credit-notes',
      'navigation.backOffice.payments.creditNotesList.blocked': 'payments/credit-notes/blocked',
      'navigation.backOffice.payments.bankTransfersList': 'payments/bank-transfers-list',
      'navigation.backOffice.payments.invoicesList': 'payments/invoices-list',
      'navigation.backOffice.payments.mpesa': 'payments/mpesa',
      'navigation.backOffice.payments.mpesa.expired': 'payments/mpesa/expired',
      'navigation.backOffice.payments.mpesa.blocked': 'payments/mpesa/blocked',
    },
    pricing: {
      'navigation.backOffice.pricing.groups': 'pricing/groups',
      'navigation.backOffice.pricing.listings': 'pricing/listings',
      'navigation.backOffice.pricing.listings.historyLog': 'pricing/listings/history-log',
      'navigation.backOffice.pricing.listings.deleted': 'pricing/listings/deleted',
      'navigation.backOffice.pricing.discounts': 'pricing/discounts',
      'navigation.backOffice.pricing.taxes': 'pricing/taxes',
    },
  };

  const getHrefFromPermission = (appName: string): string => {
    const appPermissions = navigationDictionary[appName];
    if (!appPermissions || !userPermissions) return;

    for (const permission in appPermissions) {
      if (userPermissions.includes(permission)) {
        return appPermissions[permission];
      }
    }
  };

  const sidebarItemList = useMemo<ISidebarItem[]>(() => {
    const allLinks: ISidebarItem[] = [
      {
        href: getHrefFromPermission('customers'),
        appName: 'customers',
        displayName: 'Customers',
        permissions: [
          'navigation.backOffice.customers',
          'navigation.backOffice.customers.deleted',
          'navigation.backOffice.customers.types',
        ],
        iconUrl: customersIcon,
      },
      {
        href: getHrefFromPermission('users'),
        appName: 'users',
        displayName: 'Users',
        permissions: [
          'navigation.backOffice.users',
          'navigation.backOffice.users.availability',
          'navigation.backOffice.users.roles',
          'navigation.backOffice.users.types',
        ],
        iconUrl: usersIcon,
      },
      {
        href: getHrefFromPermission('vehicles'),
        appName: 'vehicles',
        displayName: 'Vehicles',
        permissions: [
          'navigation.backOffice.vehicles',
          'navigation.backOffice.vehicles.categories',
          'navigation.backOffice.vehicles.types',
        ],
        iconUrl: assetsIcon,
      },
      {
        href: getHrefFromPermission('containers'),
        appName: 'containers',
        displayName: 'Containers',
        permissions: ['navigation.backOffice.containers', 'navigation.backOffice.containers.types'],
        iconUrl: containerIcon,
      },

      {
        href: getHrefFromPermission('stockAdmin'),
        appName: 'stock-admin',
        displayName: 'Stock Admin',
        permissions: [
          'navigation.backOffice.stockAdmin',
          'navigation.backOffice.stockAdmin.product.capacities',
          'navigation.backOffice.stockAdmin.product.categories',
          'navigation.backOffice.stockAdmin.product.saleTypes',
          'navigation.backOffice.stockAdmin.product.states',
        ],
        iconUrl: cylinderIcon,
      },
      {
        href: getHrefFromPermission('stockFlow'),
        appName: 'stock-flow',
        displayName: 'Stock Flow',
        permissions: [
          'navigation.backOffice.stockFlow',
          'navigation.backOffice.stockFlow.fillingOrders',
          'navigation.backOffice.stockFlow.defectives',
          'navigation.backOffice.stockFlow.stockMovement',
          'navigation.backOffice.stockFlow.manufacturing',
          'navigation.backOffice.stockFlow.stockIntroduction',
        ],
        iconUrl: stockIcon,
      },
      {
        href: getHrefFromPermission('shopSelling'),
        appName: 'shop-selling',
        displayName: 'Shop selling',
        permissions: ['navigation.backOffice.shopSelling.shops'],
        iconUrl: shopSellingIcon,
        hasFeatureEnabled: hasShopSellingFeatureEnabled,
      },
      {
        href: getHrefFromPermission('trips'),
        appName: 'trips',
        displayName: 'Trips',
        permissions: [
          'navigation.backOffice.trips',
          'navigation.backOffice.trips.active',
          'navigation.backOffice.trips.past',
          'navigation.backOffice.trips.pending',
        ],
        iconUrl: tripsIcon,
      },
      {
        href: getHrefFromPermission('assignments'),
        appName: 'assignments',
        displayName: 'Assignments',
        permissions: [
          'navigation.backOffice.assignments.containers',
          'navigation.backOffice.assignments.tillNumbers',
          'navigation.backOffice.assignments.vehicles',
        ],
        iconUrl: assignmentsIcon,
      },
      {
        href: getHrefFromPermission('variance'),
        appName: 'variance',
        displayName: 'Variance',
        permissions: ['navigation.backOffice.variance.active', 'navigation.backOffice.variance.past'],
        iconUrl: varianceIcon,
      },
      {
        href: getHrefFromPermission('payments'),
        appName: 'payments',
        displayName: 'Payments',
        permissions: [
          'navigation.backOffice.payments',
          'navigation.backOffice.payments.creditNotesList',
          'navigation.backOffice.payments.creditNotesList.blocked',
          'navigation.backOffice.payments.bankTransfersList',
          'navigation.backOffice.payments.invoicesList',
          'navigation.backOffice.payments.mpesa',
          'navigation.backOffice.payments.mpesa.expired',
          'navigation.backOffice.payments.mpesa.blocked',
        ],
        iconUrl: paymentsIcon,
      },
      {
        href: getHrefFromPermission('pricing'),
        appName: 'pricing',
        displayName: 'Pricing',
        permissions: [
          'navigation.backOffice.pricing.groups',
          'navigation.backOffice.pricing.listings',
          'navigation.backOffice.pricing.listings.historyLog',
          'navigation.backOffice.pricing.listings.deleted',
          'navigation.backOffice.pricing.discounts',
          'navigation.backOffice.pricing.taxes',
        ],
        iconUrl: pricingIcon,
      },
      {
        href: 'configurations',
        appName: 'configurations',
        displayName: 'Configurations',
        permissions: [''],
        iconUrl: configsIcon,
      },
    ];

    const allowedLinks: ISidebarItem[] = [];

    for (const link of allLinks) {
      if (typeof link.hasFeatureEnabled === 'boolean' && !link.hasFeatureEnabled) continue;

      // unlikely to happen, but just in case
      if (link.permissions.length === 0) {
        allowedLinks.push(link);
      }

      if (hasAnyPermission(userPermissions || [], link.permissions)) {
        allowedLinks.push(link);
      }
    }
    return allowedLinks;
  }, [userPermissions, hasShopSellingFeatureEnabled, hasProductListFeatureEnabled]);

  return { isAuthorized, sidebarItemList };
}
